import { useCallback, useEffect, useState } from 'react';
import { AdminsApi } from '../../api/resources';
import { errorMessage } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import Can, { useCan } from '../../components/Can';
import AdminFormModal from './AdminFormModal';
import PermissionsModal from './PermissionsModal';
import ConfirmModal from '../../components/ConfirmModal';
import Avatar from '../../components/Avatar';
import Icon from '../../components/Icon';

export default function AdminsPage() {
  const { user } = useAuth();
  const canCreate = useCan('admins.create');
  const canUpdate = useCan('admins.update');
  const canDelete = useCan('admins.delete');
  const canAssign = useCan('permissions.assign');

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // modal state: { mode: 'create' | 'edit' | 'perms', admin }
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await AdminsApi.list({ page, search: search || undefined });
      setRows(data.data);
      setMeta(data.meta);
    } catch (err) {
      setError(errorMessage(err, 'Could not load admins.'));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async () => {
    const admin = modal.admin;
    try {
      await AdminsApi.remove(admin.id);
      setModal(null);
      load();
    } catch (err) {
      setModal(null);
      setError(errorMessage(err, 'Could not delete admin.'));
    }
  };

  const closeModal = () => setModal(null);
  const afterSave = () => {
    setModal(null);
    load();
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Admins</h1>
          <p className="page-sub">Manage who can access this panel and what they can do.</p>
        </div>
        <Can permission="admins.create">
          <button className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>
            <Icon name="plus" size={16} />
            New admin
          </button>
        </Can>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      <div className="card">
        <div className="card-head">
          <div className="search">
            <Icon name="search" />
            <input
              type="search"
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
          </div>
          {meta && <span className="muted">{meta.total ?? rows.length} total</span>}
        </div>

        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Admin</th>
                <th>Roles</th>
                <th>Direct permissions</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                [0, 1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td><span className="skeleton" style={{ width: 180 }} /></td>
                    <td><span className="skeleton" style={{ width: 80 }} /></td>
                    <td><span className="skeleton" style={{ width: 140 }} /></td>
                    <td><span className="skeleton" style={{ width: 70, marginLeft: 'auto' }} /></td>
                  </tr>
                ))}

              {!loading &&
                rows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="person">
                        <Avatar name={a.name} />
                        <div>
                          <div className="name">
                            {a.name}
                            {a.id === user?.id && (
                              <span className="badge badge-plain" style={{ marginLeft: 8 }}>you</span>
                            )}
                          </div>
                          <div className="sub">{a.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {a.roles.length ? (
                        a.roles.map((r) => (
                          <span key={r} className="badge">
                            {r}
                          </span>
                        ))
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {a.is_super_admin ? (
                        <span className="badge badge-ok">All (super-admin)</span>
                      ) : a.permissions.length ? (
                        a.permissions.map((p) => (
                          <span key={p} className="badge badge-plain">
                            {p}
                          </span>
                        ))
                      ) : (
                        <span className="muted">None</span>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        {canAssign && !a.is_super_admin && (
                          <button
                            className="icon-btn"
                            title="Permissions"
                            aria-label={`Permissions for ${a.name}`}
                            onClick={() => setModal({ mode: 'perms', admin: a })}
                          >
                            <Icon name="key" />
                          </button>
                        )}
                        {canUpdate && (
                          <button
                            className="icon-btn"
                            title="Edit"
                            aria-label={`Edit ${a.name}`}
                            onClick={() => setModal({ mode: 'edit', admin: a })}
                          >
                            <Icon name="edit" />
                          </button>
                        )}
                        {canDelete && a.id !== user?.id && !a.is_super_admin && (
                          <button
                            className="icon-btn danger"
                            title="Delete"
                            aria-label={`Delete ${a.name}`}
                            onClick={() => setModal({ mode: 'delete', admin: a })}
                          >
                            <Icon name="trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!loading && rows.length === 0 && (
          <div className="empty">
            <div className="empty-icon">
              <Icon name="inbox" size={26} />
            </div>
            <strong>No admins found</strong>
            {search ? 'Try a different search term.' : 'Create the first admin to get started.'}
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div className="pager">
            <span className="muted">
              Page {meta.current_page} of {meta.last_page}
            </span>
            <div className="toolbar">
              <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <button
                className="btn btn-sm"
                disabled={page >= meta.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {!canCreate && !canUpdate && !canDelete && !canAssign && (
        <p className="muted" style={{ marginTop: 12 }}>
          You have read-only access to admins.
        </p>
      )}

      {modal?.mode === 'create' && (
        <AdminFormModal onClose={closeModal} onSaved={afterSave} />
      )}
      {modal?.mode === 'edit' && (
        <AdminFormModal admin={modal.admin} onClose={closeModal} onSaved={afterSave} />
      )}
      {modal?.mode === 'delete' && (
        <ConfirmModal
          title="Delete admin"
          message={`Delete ${modal.admin.name}? This cannot be undone.`}
          onConfirm={doDelete}
          onClose={closeModal}
        />
      )}
      {modal?.mode === 'perms' && (
        <PermissionsModal admin={modal.admin} onClose={closeModal} onSaved={afterSave} />
      )}
    </div>
  );
}
